import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import ConfirmDialog from '@/components/results/confirmDialog/confirmDialog.vue';
import { Confirmed } from '@/components/results/confirmDialog/confirmDialog';
import { debitAction, DebitCase, loadDebitCases, loadRuns, Run, runAction } from '@/results/results.api';
import { outcomesText, whenText } from '@/results/format';

export type QueueKind = 'approvals' | 'runs' | 'debits';

const ACTION_NAMES: Record<string, string> = {
  SETTLE: 'Result', RESETTLE: 'Correction', VOID: 'No result', UNSETTLE: 'Undo result', VOID_BETS: 'Void bets', CLOSE: 'Close',
};
const STATUS_NAMES: Record<string, string> = {
  AWAITING_APPROVAL: 'Needs approval', RUNNING: 'Running', DONE: 'Done', BLOCKED: 'Blocked', CANCELLED: 'Cancelled',
};
const RUN_FILTERS = [{ key: 'BLOCKED', label: 'Blocked' }, { key: 'FORCED', label: 'Forced' }, { key: 'RECENT', label: 'Latest' }];
const DEBIT_FILTERS = [{ key: 'OPEN', label: 'Open' }, { key: 'ALL', label: 'All' }];

/**
 * GS-903 — the operator queues on the result site (MATCH-ODDS-ENGINE.md §5.2, D2, §8): the checker's queue (runs
 * AWAITING_APPROVAL: approve, or reject / withdraw with a reason), the runs to watch (BLOCKED: retry or cancel; the forced
 * ones; the latest) and the debit cases (re-check, write off with a reason). Who may do what is gs-settlement-service's
 * (RESULT_SUPERVISOR); a refusal comes back in its own words.
 */
@Component({ components: { ConfirmDialog } })
export default class QueuePanel extends Vue {
  @Prop({ required: true }) readonly kind!: QueueKind;
  @Prop({ default: '' }) readonly username!: string;

  private items: Array<Run | DebitCase> = [];
  private loading = false;
  private error = '';
  private busy: number | null = null;
  private filter = '';
  private dialog = { open: false, title: '', eyebrow: '', lines: [] as string[], confirmLabel: '', error: '', busy: false,
    target: null as Run | DebitCase | null };

  get title(): string {
    return this.kind === 'approvals' ? 'Approvals' : this.kind === 'runs' ? 'Runs' : 'Debit cases';
  }

  get subtitle(): string {
    return this.kind === 'approvals' ? 'Corrections and void bets wait here for a second supervisor.'
      : this.kind === 'runs' ? 'Settlements that stopped, were forced, or ran lately.'
        : 'Players a correction left owing money. Their account is locked until the case closes.';
  }

  get filters(): Array<{ key: string; label: string }> {
    return this.kind === 'runs' ? RUN_FILTERS : this.kind === 'debits' ? DEBIT_FILTERS : [];
  }

  get emptyTitle(): string {
    return this.kind === 'approvals' ? 'Nothing waits for approval' : this.kind === 'runs' ? 'No run here' : 'No debit case here';
  }

  get emptyText(): string {
    return this.kind === 'runs' && this.filter === 'BLOCKED' ? 'Every settlement ran through.' : 'All clear.';
  }

  created(): void {
    this.init();
  }

  @Watch('kind')
  onKind(): void {
    this.items = [];
    this.init();
  }

  private init(): void {
    this.filter = this.kind === 'runs' ? 'BLOCKED' : this.kind === 'debits' ? 'OPEN' : '';
    this.load();
  }

  setFilter(key: string): void {
    this.filter = key;
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      if (this.kind === 'approvals') {
        this.items = await loadRuns({ status: 'AWAITING_APPROVAL' });
      } else if (this.kind === 'runs') {
        this.items = await loadRuns(this.filter === 'BLOCKED' ? { status: 'BLOCKED' } : this.filter === 'FORCED' ? { forced: true }
          : { limit: 50 });
      } else {
        this.items = await loadDebitCases(this.filter === 'OPEN' ? 'OPEN' : undefined);
      }
    } catch (e) {
      this.error = (e as Error).message;
    } finally {
      this.loading = false;
    }
  }

  // ------------------------------------------------------------------------------------------------ runs

  isMine(r: Run): boolean {
    return !!this.username && r.requestedBy === this.username;
  }

  actionName(r: Run): string {
    return ACTION_NAMES[r.action] || r.action;
  }

  statusName(r: Run): string {
    return STATUS_NAMES[r.status] || r.status;
  }

  tone(r: Run): string {
    return r.status === 'BLOCKED' ? 'danger' : r.status === 'AWAITING_APPROVAL' ? 'warning' : r.status === 'RUNNING' ? 'info'
      : r.status === 'DONE' ? 'success' : 'muted';
  }

  /** What the run would do, in words. */
  proposal(r: Run): string {
    const res = r.result;
    switch (r.action) {
      case 'VOID_BETS':
        return `Void ${(r.betIds || []).length} bet${(r.betIds || []).length === 1 ? '' : 's'}` + (r.betIds ? ` (#${r.betIds.join(', #')})` : '');
      case 'UNSETTLE':
        return 'Undo the result: every player goes back to before it';
      case 'VOID':
        return 'No result' + (res && res.voidReason ? ` (${res.voidReason.replace(/_/g, ' ').toLowerCase()})` : '') + ': every bet refunded';
      default:
        if (!res) {
          return '';
        }
        if (res.winnerName || res.winnerSelectionId) {
          return (r.action === 'RESETTLE' ? 'Change the winner to ' : 'Winner: ') + (res.winnerName || res.winnerSelectionId);
        }
        if (res.resultValue !== null && res.resultValue !== undefined) {
          const view = r.fancyKind === 'DIGIT' ? ', digit ' + res.resultView : r.fancyKind === 'PARITY' ? (res.resultView === 1 ? ', odd' : ', even') : '';
          return (r.action === 'RESETTLE' ? 'Change the result to ' : 'Result ') + res.resultValue + view;
        }
        // GS-894: a premium result as the statement reads it — "Over 2.25 Half won, Under 2.25 Half lost"
        return res.outcomes ? (r.action === 'RESETTLE' ? 'Change the result to ' : 'Result: ') + outcomesText(res.outcomes) : '';
    }
  }

  progress(r: Run): number {
    return r.chunksTotal ? Math.round((100 * r.chunksDone) / r.chunksTotal) : 0;
  }

  when(at: string | null): string {
    return whenText(at);
  }

  async approve(r: Run): Promise<void> {
    await this.act(r.runId, async () => {
      const run = await runAction(r.runId, 'approve');
      this.$emit('toast', 'success', `${r.marketName || r.marketId}: approved — ${run && run.status === 'DONE' ? 'settled' : 'settling'}.`);
    });
  }

  async retry(r: Run): Promise<void> {
    await this.act(r.runId, async () => {
      await runAction(r.runId, 'retry');
      this.$emit('toast', 'info', `${r.marketName || r.marketId}: run ${r.runId} retried.`);
    });
  }

  askCancel(r: Run): void {
    const mine = this.isMine(r);
    this.dialog = {
      open: true, target: r, error: '', busy: false,
      eyebrow: r.status === 'AWAITING_APPROVAL' ? (mine ? 'Withdraw a request' : 'Reject') : 'Cancel a run',
      title: `${this.actionName(r)} on ${r.marketName || r.marketId}`,
      lines: [(r.eventName || '') + (r.requestedBy ? ' · asked by ' + r.requestedBy : ''), this.proposal(r)].filter((l) => !!l),
      confirmLabel: r.status === 'AWAITING_APPROVAL' ? (mine ? 'Withdraw' : 'Reject') : 'Cancel the run',
    };
  }

  // ------------------------------------------------------------------------------------------------ debit cases

  caseState(c: DebitCase): string {
    return c.state === 'OPEN' ? 'Open' : c.state === 'RESOLVED' ? 'Paid' : c.state === 'WRITTEN_OFF' ? 'Written off' : c.state;
  }

  locks(c: DebitCase): string {
    if (!c.account) {
      return '';
    }
    const locks = [c.account.betLock ? 'betting locked' : '', c.account.withdrawLock ? 'withdrawals locked' : ''].filter((l) => !!l);
    return locks.length ? locks.join(', ') : 'unlocked';
  }

  money(amount: string | null | undefined): string {
    return Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  async recheck(c: DebitCase): Promise<void> {
    await this.act(c.caseId, async () => {
      const after = await debitAction(c.caseId, 'recheck');
      this.$emit('toast', after && after.state === 'OPEN' ? 'info' : 'success',
        after && after.state === 'OPEN' ? `${c.username}: still owes ${this.money(after.shortfallNow)}.` : `${c.username}: paid — the case is closed.`);
    });
  }

  askWriteOff(c: DebitCase): void {
    this.dialog = {
      open: true, target: c, error: '', busy: false, eyebrow: 'Write off',
      title: `Write off ${this.money(c.shortfallNow)} for ${c.username || 'player ' + c.userId}`,
      lines: [c.eventName || c.marketId, 'The house carries what the player cannot pay; the case closes.'],
      confirmLabel: 'Write off',
    };
  }

  // ------------------------------------------------------------------------------------------------ the rest

  async confirmDialog(c: Confirmed): Promise<void> {
    const target = this.dialog.target;
    if (!target) {
      return;
    }
    this.dialog.busy = true;
    this.dialog.error = '';
    try {
      if ('caseId' in target) {
        await debitAction(target.caseId, 'write-off', c.reason);
        this.$emit('toast', 'success', `${target.username}: written off.`);
      } else {
        await runAction(target.runId, 'cancel', c.reason);
        this.$emit('toast', 'success', `${target.marketName || target.marketId}: run ${target.runId} ${target.status === 'AWAITING_APPROVAL' ? 'withdrawn' : 'cancelled'}.`);
      }
      this.dialog.open = false;
      this.$emit('changed');
      await this.load();
    } catch (e) {
      this.dialog.error = (e as Error).message;
    } finally {
      this.dialog.busy = false;
    }
  }

  private async act(id: number, work: () => Promise<void>): Promise<void> {
    this.busy = id;
    try {
      await work();
      this.$emit('changed');
      await this.load();
    } catch (e) {
      this.$emit('toast', 'error', (e as Error).message);
    } finally {
      this.busy = null;
    }
  }
}
